import React, { useCallback, useEffect, useMemo, useState } from "react";

import { endOfDay, startOfDay, subDays } from "date-fns";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import CfLoader from "../../common/CfLoader/CfLoader";
import PageErrorState from "../../common/PageErrorState/PageErrorState";
import DateFilterDropdown from "../../components/DateFilterDropdown/DateFilterDropdown";
import IssuanceBillingTab from "../../components/Finance/IssuanceBillingTab";
import VerificationSettlementTab from "../../components/Finance/VerificationSettlementTab";
import EntityConstants from "../../configs/EntityConstants";
import { GET } from "../../service/CRUDService";
import {
  classifyApiError,
  getApiErrorCopy,
} from "../../utils/classifyApiError";
import { parseSuccessEnvelope } from "../../utils/parseSuccessEnvelope";

export const FINANCE_ROUTES = {
  root: "/issuer/finance",
  issuance: "/issuer/finance/issuance-billing",
  settlement: "/issuer/finance/verification-settlement",
};

const TAB_ISSUANCE = "issuance";
const TAB_SETTLEMENT = "settlement";

const getTabFromPath = (pathname) => {
  if (pathname.startsWith(FINANCE_ROUTES.settlement)) {
    return TAB_SETTLEMENT;
  }
  if (pathname.startsWith(FINANCE_ROUTES.issuance)) {
    return TAB_ISSUANCE;
  }
  return null;
};

const FinancePage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState("recent");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [loadingCapabilities, setLoadingCapabilities] = useState(true);
  const [capabilitiesError, setCapabilitiesError] = useState(null);

  const dateParams = useMemo(() => {
    const toIso = (date) => (date ? new Date(date).toISOString() : "");
    const now = new Date();
    let startDate;
    let endDate;

    switch (dateRange) {
      case "recent":
        startDate = subDays(startOfDay(now), 6);
        endDate = endOfDay(now);
        break;
      case "last30days":
        startDate = subDays(startOfDay(now), 29);
        endDate = endOfDay(now);
        break;
      case "last3months":
        startDate = subDays(startOfDay(now), 89);
        endDate = endOfDay(now);
        break;
      case "last6months":
        startDate = subDays(startOfDay(now), 179);
        endDate = endOfDay(now);
        break;
      case "all":
        return {};
      case "custom":
        if (customStartDate && customEndDate) {
          startDate = startOfDay(customStartDate);
          endDate = endOfDay(customEndDate);
        } else {
          startDate = subDays(startOfDay(now), 6);
          endDate = endOfDay(now);
        }
        break;
      default:
        startDate = subDays(startOfDay(now), 6);
        endDate = endOfDay(now);
    }

    return {
      start_date: toIso(startDate),
      end_date: toIso(endDate),
    };
  }, [dateRange, customStartDate, customEndDate]);

  const loadCapabilities = useCallback(async () => {
    setLoadingCapabilities(true);
    setCapabilitiesError(null);
    try {
      const response = await GET(EntityConstants.FINANCE_CAPABILITIES);
      const data = await parseSuccessEnvelope(response);
      setCapabilities(data);
    } catch (error) {
      setCapabilities(null);
      setCapabilitiesError(classifyApiError(error));
    } finally {
      setLoadingCapabilities(false);
    }
  }, []);

  useEffect(() => {
    loadCapabilities();
  }, [loadCapabilities]);

  const issuanceEnabled = capabilities?.issuance_billing?.enabled === true;
  const settlementEnabled =
    capabilities?.verification_settlement?.enabled === true;

  const tabs = useMemo(() => {
    const items = [];
    if (issuanceEnabled) {
      items.push({
        id: TAB_ISSUANCE,
        label: t("finance:tab_issuance"),
        path: FINANCE_ROUTES.issuance,
      });
    }
    if (settlementEnabled) {
      items.push({
        id: TAB_SETTLEMENT,
        label: t("finance:tab_settlement"),
        path: FINANCE_ROUTES.settlement,
      });
    }
    return items;
  }, [issuanceEnabled, settlementEnabled, t]);

  const defaultFinancePath = useMemo(() => {
    if (issuanceEnabled) return FINANCE_ROUTES.issuance;
    if (settlementEnabled) return FINANCE_ROUTES.settlement;
    return null;
  }, [issuanceEnabled, settlementEnabled]);

  const activeTab = getTabFromPath(location.pathname);

  const routeRedirect = useMemo(() => {
    if (loadingCapabilities || capabilitiesError || !capabilities) {
      return null;
    }

    if (!defaultFinancePath) {
      return null;
    }

    const onRoot =
      location.pathname === FINANCE_ROUTES.root ||
      location.pathname === `${FINANCE_ROUTES.root}/`;

    if (onRoot) {
      return defaultFinancePath;
    }

    if (activeTab === TAB_ISSUANCE && !issuanceEnabled) {
      return defaultFinancePath;
    }

    if (activeTab === TAB_SETTLEMENT && !settlementEnabled) {
      return defaultFinancePath;
    }

    if (
      activeTab !== TAB_ISSUANCE &&
      activeTab !== TAB_SETTLEMENT &&
      location.pathname.startsWith(FINANCE_ROUTES.root)
    ) {
      return defaultFinancePath;
    }

    return null;
  }, [
    loadingCapabilities,
    capabilitiesError,
    capabilities,
    defaultFinancePath,
    location.pathname,
    activeTab,
    issuanceEnabled,
    settlementEnabled,
  ]);

  const errorCopy = capabilitiesError
    ? getApiErrorCopy(capabilitiesError, t)
    : null;

  if (routeRedirect) {
    return <Navigate to={routeRedirect} replace />;
  }

  return (
    <div className="mt-20 bg-cfSecondary min-h-screen p-4 sm:p-6">
      <div className="flex flex-col gap-5 mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-cfHeadingText">
              {t("finance:title")}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {t("finance:subtitle")}
            </p>
          </div>
          {!capabilitiesError && !loadingCapabilities && tabs.length > 0 ? (
            <div className="sm:min-w-[10rem] self-start shrink-0">
              <DateFilterDropdown
                dateRange={dateRange}
                setDateRange={setDateRange}
                customStartDate={customStartDate}
                setCustomStartDate={setCustomStartDate}
                customEndDate={customEndDate}
                setCustomEndDate={setCustomEndDate}
              />
            </div>
          ) : null}
        </div>

        {loadingCapabilities ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <CfLoader />
            <p className="text-sm text-gray-500">
              {t("finance:loading_capabilities")}
            </p>
          </div>
        ) : capabilitiesError ? (
          <PageErrorState
            kind={capabilitiesError.kind}
            title={errorCopy.title}
            description={errorCopy.description}
            primaryLabel={t("finance:try_again")}
            onPrimary={loadCapabilities}
            secondaryLabel={t("finance:refresh_page")}
            onSecondary={() => window.location.reload()}
            isRetrying={loadingCapabilities}
            details={
              capabilitiesError.status
                ? t("finance:error_details_status", {
                    status: capabilitiesError.status,
                  })
                : null
            }
          />
        ) : tabs.length === 0 ? (
          <PageErrorState
            kind="not_found"
            title={t("finance:no_capabilities")}
            description={t("finance:no_capabilities_description")}
            primaryLabel={t("finance:try_again")}
            onPrimary={loadCapabilities}
          />
        ) : (
          <>
            {tabs.length > 1 ? (
              <div
                className="inline-flex self-start p-1 bg-white border border-gray-200 rounded-full shadow-sm gap-1"
                role="tablist"
                aria-label={t("finance:title")}
              >
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => navigate(tab.path)}
                      className={`px-4 sm:px-5 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                        isActive
                          ? "bg-cfPrimaryButton text-white shadow-sm"
                          : "text-gray-700 hover:bg-cfTableHeaderBackgroundColor"
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            ) : null}

            {activeTab === TAB_ISSUANCE && issuanceEnabled ? (
              <IssuanceBillingTab dateParams={dateParams} />
            ) : null}

            {activeTab === TAB_SETTLEMENT && settlementEnabled ? (
              <VerificationSettlementTab dateParams={dateParams} />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default FinancePage;
